import React from 'react';
import { Service, inject } from '@piwikpro/platform';
import type { Store } from 'redux';
import { createAction } from 'redux-actions';

export interface ConfirmationAttributes {
  onSubmit?: (value: unknown) => void
  onCancel?: () => void
  onError?: (error: unknown) => void | boolean
  messages?: Array<string>
  title: string
  name?: string
  applyBtnText?: string
  applyBtnAppearance?: string
  cancelBtnText?: string
  type?: 'simple' | 'customBody' | 'withCheck'
  asyncAction?: () => Promise<void>
}

class ConfirmationRejectError extends Error {
  name = 'ConfirmationRejectError';
}

export const confirmationInitialized = createAction<ConfirmationAttributes, ConfirmationAttributes>(
  '@system/event/confirmations/show',
  (payload: ConfirmationAttributes) => payload,
);
export const confirmationSucceeded = createAction(
  '@system/event/confirmations/pass',
);
export const confirmationCancelled = createAction(
  '@system/event/confirmations/cancel',
);
export const confirmationFailed = createAction(
  '@system/event/confirmations/reject',
);

@Service()
export class Confirmation {
  private customModal: {
    body: React.FunctionComponent | null
  }

  constructor(
    @inject('store') private readonly store: Store,
  ) {
    this.customModal = {
      body: null,
    };
  }

  private setCustomModal(body: React.FunctionComponent | null) {
    this.customModal = {
      body,
    };
  }

  public getCustomBody(): React.FunctionComponent | null {
    return this.customModal.body;
  }

  confirm(
    attributes: ConfirmationAttributes,
    customBody?: React.FunctionComponent,
  ) {
    if (customBody) {
      this.setCustomModal(customBody || null);
    }

    return new Promise((resolve, reject) => {
      this.store.dispatch(confirmationInitialized({
        onSubmit: resolve,
        onCancel: () => reject(new ConfirmationRejectError()),
        onError: (error) => {
          // Default: keep the modal open so the user can retry.
          // Return true from onError to close the modal and reject confirm().
          const shouldClose = attributes.onError?.(error);
          if (shouldClose) {
            reject(error);
          }
        },
        messages: attributes.messages,
        title: attributes.title,
        name: attributes.name,
        type: attributes.type || 'simple',
        applyBtnText: attributes.applyBtnText,
        applyBtnAppearance: attributes.applyBtnAppearance,
        cancelBtnText: attributes.cancelBtnText,
        asyncAction: attributes.asyncAction,
      }));
    }).then(() => {
      this.store.dispatch(confirmationSucceeded());
    }).catch((err) => {
      this.store.dispatch(confirmationCancelled());
      throw err;
    });
  }
}
